import { useParams, Link, Navigate } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Mail, ArrowLeft } from 'lucide-react';

const SUPPORT_EMAIL = 'tyler@tradesmateuk.co.uk';
const LAST_UPDATED = '21 June 2026';

const POLICIES = {
  privacy: {
    title: 'Privacy Policy',
    intro:
      'This Privacy Policy explains what personal information TradesMate UK (“TradesMate”, “we”, “our”, “us”) collects when you use https://tradesmateuk.co.uk and the connected web application, how we use it, who we share it with, and your rights under UK GDPR.',
    sections: [
      {
        h: '1. Who we are',
        body: (
          <p>
            TradesMate UK is a software tool that helps UK tradespeople build quotes, run trade calculators and manage customer quotes. The
            data controller is the operator of TradesMate UK. You can contact us at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline text-brand">{SUPPORT_EMAIL}</a>.
          </p>
        ),
      },
      {
        h: '2. What we collect',
        body: (
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account information</strong> — name, email address, password (hashed using bcrypt), business name, optional business address, phone number, VAT number and logo image.</li>
            <li><strong>Quote &amp; calculator inputs</strong> — line items, materials, labour, customer name/address/email/phone, notes and amounts you enter into the estimator and calculators.</li>
            <li><strong>Subscription information</strong> — your TradesMate plan (Free / Pro / Pro+), Stripe customer ID, subscription status. <strong>Payment card details are handled entirely by Stripe and never reach our servers.</strong></li>
            <li><strong>Usage analytics</strong> — anonymised pageviews (path, timestamp and a hashed visitor identifier). We do not use third-party trackers such as Google Analytics or Facebook Pixel.</li>
            <li><strong>Cookies</strong> — see our <Link to="/legal/cookies" className="underline text-brand">Cookie Policy</Link>. Only strictly-necessary cookies are used.</li>
            <li><strong>Communications</strong> — any message you send us by email or via the in-app feedback form.</li>
          </ul>
        ),
      },
      {
        h: '3. Why we collect it (lawful basis)',
        body: (
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>To provide the service</strong> (Article 6(1)(b) UK GDPR — performance of a contract): saving your quotes, running calculations, generating PDFs, emailing share-link notifications.</li>
            <li><strong>To take payment</strong> (Article 6(1)(b)): managing your subscription via Stripe.</li>
            <li><strong>To keep the service secure</strong> (Article 6(1)(f) — legitimate interest): rate-limiting, authentication cookies, audit logs.</li>
            <li><strong>To improve TradesMate</strong> (Article 6(1)(f)): anonymised pageview analytics.</li>
          </ul>
        ),
      },
      {
        h: '4. Who we share it with',
        body: (
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Stripe</strong> — subscription billing and payment processing (<a href="https://stripe.com/gb/privacy" className="underline text-brand" target="_blank" rel="noreferrer">stripe.com/privacy</a>).</li>
            <li><strong>Resend</strong> — transactional email delivery for share-link notifications and account emails (<a href="https://resend.com/legal/privacy-policy" className="underline text-brand" target="_blank" rel="noreferrer">resend.com privacy</a>).</li>
            <li><strong>OpenAI</strong> — only when you use the AI Estimator: the prompt text you send is processed by OpenAI to generate the response (<a href="https://openai.com/policies/privacy-policy" className="underline text-brand" target="_blank" rel="noreferrer">openai.com privacy</a>). Do not include sensitive personal information in AI prompts.</li>
            <li><strong>Hosting / infrastructure providers</strong> — for running and securing the service.</li>
          </ul>
        ),
      },
      {
        h: '5. International transfers',
        body: <p>Some of our processors (Stripe, Resend, OpenAI) are based outside the UK. Where data is transferred outside the UK, we rely on the UK’s adequacy regulations or standard contractual clauses to protect it.</p>,
      },
      {
        h: '6. How long we keep it',
        body: (
          <ul className="list-disc pl-6 space-y-2">
            <li>Account data: while your account is active, plus up to 30 days after deletion.</li>
            <li>Quotes and customer data you enter: while your account is active. You can delete individual quotes at any time.</li>
            <li>Payment records: kept for 6 years to comply with HMRC record-keeping rules.</li>
            <li>Pageview analytics: kept for 24 months in anonymised form.</li>
          </ul>
        ),
      },
      {
        h: '7. Your rights',
        body: (
          <>
            <p>Under UK GDPR you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Access a copy of your personal data.</li>
              <li>Have inaccurate data corrected.</li>
              <li>Request erasure (right to be forgotten).</li>
              <li>Object to or restrict processing.</li>
              <li>Receive your data in a portable format.</li>
              <li>Withdraw consent at any time where consent is the basis for processing.</li>
              <li>Lodge a complaint with the Information Commissioner’s Office (<a href="https://ico.org.uk/" className="underline text-brand" target="_blank" rel="noreferrer">ico.org.uk</a>).</li>
            </ul>
            <p className="mt-2">Email <a href={`mailto:${SUPPORT_EMAIL}`} className="underline text-brand">{SUPPORT_EMAIL}</a> to exercise any of these rights.</p>
          </>
        ),
      },
      {
        h: '8. Security',
        body: <p>Passwords are hashed with bcrypt. Authentication uses HTTP-only, secure JWT cookies. Connections are encrypted with HTTPS. No system is 100% secure — if you suspect a security issue, email us immediately.</p>,
      },
      {
        h: '9. Changes to this policy',
        body: <p>We may update this Privacy Policy from time to time. The latest version will always be at this URL with an updated &quot;Last updated&quot; date.</p>,
      },
    ],
  },

  terms: {
    title: 'Terms of Service',
    intro:
      'These Terms of Service (“Terms”) govern your use of TradesMate UK and form a contract between you and us. By creating an account or using TradesMate you agree to them. If you do not agree, do not use the service.',
    sections: [
      {
        h: '1. The service',
        body: <p>TradesMate UK provides software tools for UK tradespeople including trade calculators, an AI estimator, quote/invoice generation, PDF export and customer share-link tracking. We can change, suspend or discontinue features at any time, with reasonable notice where possible.</p>,
      },
      {
        h: '2. Estimates only — NOT a binding quotation',
        body: (
          <div className="border border-brand bg-brand/10 p-4">
            <p className="font-display font-bold uppercase tracking-tight mb-2">Important disclaimer</p>
            <p>
              All outputs from TradesMate calculators, the AI Estimator and the quote builder are <strong>estimates only</strong>, intended as a starting point for the tradesperson&apos;s own professional judgement. They are <strong>not</strong> a binding quotation, take-off, or specification. Material prices, labour rates, site conditions and waste factors vary widely and must be verified by you before quoting a customer.
            </p>
            <p className="mt-2">
              You remain solely responsible for the accuracy of any quote, invoice or estimate you produce or send to a customer using TradesMate. <strong>We do not warrant that any output is accurate, complete or fit for any particular purpose.</strong>
            </p>
          </div>
        ),
      },
      {
        h: '3. Your account',
        body: (
          <ul className="list-disc pl-6 space-y-2">
            <li>You must be at least 18 and use a real name and contact details.</li>
            <li>You are responsible for all activity under your account. Keep your password safe.</li>
            <li>You may close your account at any time by emailing <a href={`mailto:${SUPPORT_EMAIL}`} className="underline text-brand">{SUPPORT_EMAIL}</a>.</li>
          </ul>
        ),
      },
      {
        h: '4. Subscriptions, billing &amp; refunds',
        body: (
          <ul className="list-disc pl-6 space-y-2">
            <li>Paid plans renew automatically until you cancel. You can cancel at any time from the Billing area; you keep access until the end of the current billing period.</li>
            <li>Payments are processed by Stripe. We do not store card details.</li>
            <li>Refunds: per the UK Consumer Contracts Regulations, you are entitled to a refund within 14 days of first purchase only if you have not used the service. Once you have generated quotes, used the AI Estimator or otherwise used Pro features, that 14-day right is waived as the digital service has been performed.</li>
            <li>Discretionary refunds outside this window may be offered case-by-case — email us.</li>
          </ul>
        ),
      },
      {
        h: '5. Acceptable use',
        body: (
          <>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Reverse-engineer, scrape, copy or attempt to extract the source code, calculator formulas, AI prompts or design of TradesMate.</li>
              <li>Resell, sub-license, or share your account.</li>
              <li>Use the service to break the law, infringe anyone&apos;s rights, defraud a customer, or send unsolicited marketing.</li>
              <li>Attempt to gain unauthorised access, disrupt the service, or probe for vulnerabilities. Responsible disclosure is welcome via <a href={`mailto:${SUPPORT_EMAIL}`} className="underline text-brand">{SUPPORT_EMAIL}</a>.</li>
              <li>Upload or send content that is unlawful, defamatory, obscene, infringing or harmful.</li>
            </ul>
            <p className="mt-2">We may suspend or terminate accounts that breach these rules.</p>
          </>
        ),
      },
      {
        h: '6. Intellectual property',
        body: <p>The TradesMate brand, calculator algorithms, source code, designs, copy, and all underlying intellectual property remain ours. You receive a personal, non-transferable, revocable licence to use the service per these Terms. You retain ownership of the customer data and quote content you enter, and grant us only the minimum licence required to host and process it for you.</p>,
      },
      {
        h: '7. Limitation of liability',
        body: (
          <div className="border border-brand bg-brand/10 p-4">
            <p>
              To the maximum extent permitted by law, TradesMate UK is not liable for any loss of profit, loss of business, loss of contracts, loss of revenue, loss of anticipated savings, loss of goodwill, or any indirect or consequential loss — including (without limitation) any under-quote, over-quote or commercial decision based on a TradesMate output.
            </p>
            <p className="mt-2">
              Our total aggregate liability for any direct loss is limited to the greater of <strong>(a) £100</strong> or <strong>(b) the fees you paid us in the 12 months immediately before the event</strong>.
            </p>
            <p className="mt-2">
              Nothing in these Terms excludes liability that cannot lawfully be excluded — such as death or personal injury caused by negligence, or fraud.
            </p>
          </div>
        ),
      },
      {
        h: '8. AI features',
        body: <p>When you use the AI Estimator your prompt text is processed by OpenAI. Outputs are generated by a statistical model and may be inaccurate, biased or out of date. Do not rely on AI outputs as legal, financial or professional advice. Do not enter sensitive personal data in prompts.</p>,
      },
      {
        h: '9. Termination',
        body: <p>You can close your account any time. We may suspend or close your account if you breach these Terms or fail to pay. On closure, your access ends and your data may be deleted in line with the Privacy Policy.</p>,
      },
      {
        h: '10. Governing law',
        body: <p>These Terms are governed by the laws of England &amp; Wales. The courts of England &amp; Wales have exclusive jurisdiction.</p>,
      },
      {
        h: '11. Contact',
        body: <p>Questions? Email <a href={`mailto:${SUPPORT_EMAIL}`} className="underline text-brand">{SUPPORT_EMAIL}</a>.</p>,
      },
    ],
  },

  cookies: {
    title: 'Cookie Policy',
    intro:
      'This page lists every cookie set by TradesMate UK and what each one does. We use only strictly-necessary cookies, so under UK PECR we do not require a separate cookie consent banner — but we publish this policy in the interests of transparency.',
    sections: [
      {
        h: '1. What we use',
        body: (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200">
              <thead>
                <tr className="bg-slate-100 text-left">
                  <th className="p-2 border-b border-slate-200 font-display uppercase tracking-tight">Name</th>
                  <th className="p-2 border-b border-slate-200 font-display uppercase tracking-tight">Purpose</th>
                  <th className="p-2 border-b border-slate-200 font-display uppercase tracking-tight">Lifetime</th>
                  <th className="p-2 border-b border-slate-200 font-display uppercase tracking-tight">Type</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                <tr><td className="p-2 border-b border-slate-200">access_token</td><td className="p-2 border-b border-slate-200">Keeps you signed in (JWT, HTTP-only, Secure)</td><td className="p-2 border-b border-slate-200">60 minutes</td><td className="p-2 border-b border-slate-200">Strictly necessary</td></tr>
                <tr><td className="p-2 border-b border-slate-200">refresh_token</td><td className="p-2 border-b border-slate-200">Refreshes your session without re-login</td><td className="p-2 border-b border-slate-200">7 days</td><td className="p-2 border-b border-slate-200">Strictly necessary</td></tr>
                <tr><td className="p-2">tm-cookie-notice</td><td className="p-2">Remembers you dismissed our cookie notice (localStorage)</td><td className="p-2">12 months</td><td className="p-2">Strictly necessary</td></tr>
              </tbody>
            </table>
          </div>
        ),
      },
      {
        h: '2. Third-party cookies',
        body: <p>Stripe Checkout may set its own cookies on the Stripe-hosted billing pages — those are governed by <a href="https://stripe.com/gb/cookie-settings" className="underline text-brand" target="_blank" rel="noreferrer">Stripe&apos;s cookie policy</a>. We do not use Google Analytics, Facebook Pixel or any other ad-tech trackers on our own site.</p>,
      },
      {
        h: '3. Managing cookies',
        body: <p>You can clear cookies any time via your browser settings. Disabling our strictly-necessary cookies will sign you out and prevent you from using authenticated features.</p>,
      },
    ],
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const policy = POLICIES[slug];
  if (!policy) return <Navigate to="/legal/privacy" replace />;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title={policy.title} description={`${policy.title} for TradesMate UK.`} path={`/legal/${slug}`} />
      <Link to="/" className="text-xs uppercase tracking-widest text-slate-500 hover:text-brand inline-flex items-center gap-1 mb-4 font-semibold">
        <ArrowLeft size={14}/> Back to TradesMate
      </Link>
      <p className="text-xs uppercase tracking-widest text-brand font-semibold">Legal</p>
      <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">{policy.title}</h1>
      <p className="text-xs uppercase tracking-widest text-slate-500 mt-2 font-semibold">Last updated: {LAST_UPDATED}</p>

      <p className="text-slate-700 leading-relaxed mt-6">{policy.intro}</p>

      <div className="mt-8 space-y-8">
        {policy.sections.map((s, idx) => (
          <section key={idx}>
            <h2 className="font-display font-semibold uppercase tracking-tight text-xl mb-3">{s.h}</h2>
            <div className="text-slate-700 leading-relaxed">{s.body}</div>
          </section>
        ))}
      </div>

      <div className="mt-12 border-t border-slate-200 pt-6 flex items-center justify-between flex-wrap gap-3">
        <Link to="/legal/privacy" className="text-xs uppercase tracking-widest hover:text-brand font-semibold">Privacy Policy</Link>
        <Link to="/legal/terms" className="text-xs uppercase tracking-widest hover:text-brand font-semibold">Terms of Service</Link>
        <Link to="/legal/cookies" className="text-xs uppercase tracking-widest hover:text-brand font-semibold">Cookie Policy</Link>
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-xs uppercase tracking-widest hover:text-brand inline-flex items-center gap-1 font-semibold">
          <Mail size={12}/> {SUPPORT_EMAIL}
        </a>
      </div>
    </div>
  );
}
